import { Shot, AssetRef, Resource } from '../../../types';
import { highlightAtRefs } from '../../../utils/highlightAtRefs';
import ShotPromptEditor from './ShotPromptEditor';
import { ShotEditFormData } from '../types';
import { DEFAULT_MAX_DURATION_SEC, DEFAULT_MIN_DURATION_SEC } from '../../../utils/videoLimits';

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface ShotPromptPanelProps {
  shot: Shot;
  hasLanguageSkill: boolean;
  languageSkillName: string;
  t: Translate;
  editing?: boolean;
  shotEdits?: ShotEditFormData;
  onShotEditsChange?: React.Dispatch<React.SetStateAction<ShotEditFormData>>;
  refs?: AssetRef[];
  resources?: Resource[];
  minDuration?: number;
  maxDuration?: number;
}

const BOX_STYLE: React.CSSProperties = {
  background: 'var(--surface-card, var(--bg))', borderRadius: 'var(--radius)',
  overflow: 'hidden', boxShadow: 'var(--shadow-prompt-box)', margin: '0.5rem 0',
};
const DURATION_ROW_STYLE: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
  padding: '0.5rem 0.75rem', fontSize: '0.85rem', fontWeight: 700, color: 'var(--theme-storyboard)',
};

function ClockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

export default function ShotPromptPanel({
  shot, hasLanguageSkill, languageSkillName, t,
  editing = false, shotEdits, onShotEditsChange, refs = [], resources = [],
  minDuration = DEFAULT_MIN_DURATION_SEC, maxDuration = DEFAULT_MAX_DURATION_SEC,
}: ShotPromptPanelProps) {
  const showFailBanner = hasLanguageSkill && shot.languageRewriteStatus === 'failed';
  const showAppliedBadge = !editing && hasLanguageSkill && shot.languageSkillAppliedAt && shot.languageRewriteStatus !== 'failed';

  return (
    <>
      {showFailBanner && (
        <div style={{
          marginBottom: '0.5rem', padding: '0.5rem 0.75rem', background: 'rgba(185, 28, 28, 0.08)',
          border: '1px solid var(--danger, #b91c1c)', borderRadius: 'var(--radius-sm)',
          color: 'var(--danger, #b91c1c)', fontSize: '0.8rem', display: 'flex', alignItems: 'flex-start', gap: '0.4rem',
        }}>
          <span style={{ flexShrink: 0, fontWeight: 500 }}>{t('shot.langRewriteFailed')}</span>
          <span style={{ wordBreak: 'break-word' }}>{shot.languageRewriteError || ''}</span>
        </div>
      )}

      {editing && shotEdits && onShotEditsChange ? (
        <div style={{ marginBottom: '1rem' }}>
          <p style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', color: 'var(--text-2)', fontWeight: 500 }}>{t('shot.promptLabel')}</p>
          <div style={BOX_STYLE}>
            <div style={DURATION_ROW_STYLE}>
              <ClockIcon />
              <span style={{ fontWeight: 500 }}>{t('shot.generationDuration')}:</span>
              <input
                type="number" className="input" min={minDuration} max={maxDuration} style={{ width: '4.5rem' }}
                value={shotEdits.duration}
                onChange={(event) => onShotEditsChange({ ...shotEdits, duration: Number(event.target.value) })}
              />
              <span style={{ fontWeight: 500 }}>{t('shot.durationSec', { n: '' })}</span>
            </div>
            <div style={{ padding: '0 0.75rem 0.75rem' }}>
              <ShotPromptEditor shotEdits={shotEdits} onShotEditsChange={onShotEditsChange} refs={refs} resources={resources} t={t} />
            </div>
          </div>
        </div>
      ) : shot.prompt ? (
        <div style={{ marginBottom: '1rem' }}>
          <p style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 500 }}>
            {t('shot.promptLabel')}
            {showAppliedBadge && (
              <span style={{ fontSize: '0.75rem', fontWeight: 400, color: 'var(--success, #15803d)' }}>
                {t('shot.langApplied', { name: languageSkillName || '' })}
              </span>
            )}
          </p>
          <div style={BOX_STYLE}>
            <div style={DURATION_ROW_STYLE}>
              <ClockIcon />
              <span style={{ fontWeight: 500 }}>{t('shot.generationDuration')}:</span>
              {t('shot.durationSec', { n: shot.duration })}
            </div>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.9rem', padding: '0 0.75rem 0.75rem' }}>{highlightAtRefs(shot.prompt)}</pre>
          </div>
        </div>
      ) : null}
    </>
  );
}
